import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, Clock, Users, ArrowLeft, Loader2, ExternalLink, CheckCircle2,
  Target, ListChecks, FileText, ChevronDown, PlayCircle, Video,
} from 'lucide-react';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const muted = { color: 'var(--p-muted)' };

const ytId = (url) => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};
const vimeoId = (url) => {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
};

const Recording = ({ url }) => {
  const yt = ytId(url);
  const vm = vimeoId(url);
  if (yt) {
    return <iframe title="Recording" src={`https://www.youtube.com/embed/${yt}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full rounded-xl border-0" style={{ aspectRatio: '16/9' }} data-testid="group-recording-frame" />;
  }
  if (vm) {
    return <iframe title="Recording" src={`https://player.vimeo.com/video/${vm}`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen className="w-full rounded-xl border-0" style={{ aspectRatio: '16/9' }} data-testid="group-recording-frame" />;
  }
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) {
    return <video controls src={url} className="w-full rounded-xl" style={{ aspectRatio: '16/9', background: '#000' }} data-testid="group-recording-video" />;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-5 py-3 rounded-lg font-medium transition-colors" data-testid="group-recording-link">
      <PlayCircle size={17} /> Watch recording <ExternalLink size={14} />
    </a>
  );
};

export default function ClientGroupSessionDetail() {
  const { groupSessionId } = useParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRoom, setShowRoom] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/group-sessions/${groupSessionId}`, { withCredentials: true })
      .then((res) => setS(res.data))
      .catch((err) => setError(err.response?.status === 404 ? 'Group session not found.' : 'Could not load this session.'))
      .finally(() => setLoading(false));
  }, [groupSessionId]);

  if (loading) {
    return <PortalLayout active="sessions"><div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div></PortalLayout>;
  }
  if (error || !s) {
    return (
      <PortalLayout active="sessions">
        <div className="p-card rounded-2xl p-10 text-center" data-testid="group-detail-error">
          <p className="mb-4" style={muted}>{error || 'Group session not found.'}</p>
          <Link to="/portal/sessions" className="inline-flex items-center gap-2 text-[#1D4ED8] font-medium"><ArrowLeft size={16} /> Back to sessions</Link>
        </div>
      </PortalLayout>
    );
  }

  const isCompleted = s.status === 'completed';
  const takeaways = s.ai_key_takeaways || [];
  const actionPlan = s.ai_action_plan || [];
  const goals = s.ai_goals || [];
  const hasRecap = s.ai_summary || takeaways.length || actionPlan.length;
  const jitsiUrl = `https://meet.jit.si/${s.room_name || `DCAGroup-${groupSessionId}`}`;

  return (
    <PortalLayout active="sessions">
      <Link to="/portal/sessions" className="inline-flex items-center gap-2 text-sm mb-6" style={muted} data-testid="back-to-sessions">
        <ArrowLeft size={15} /> All sessions
      </Link>

      <div className="p-card rounded-2xl p-6 mb-6" data-testid="group-detail-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-1.5" style={{ color: '#F97316' }}><Users size={13} /> Group Session</span>
            <h1 className="text-2xl sm:text-3xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="group-detail-title">{s.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm" style={muted}>
              {s.session_date && <span className="flex items-center gap-1.5"><Calendar size={14} className="text-[#1D4ED8]" />{s.session_date}</span>}
              {s.session_time && <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#1D4ED8]" />{s.session_time}</span>}
              <span>{s.duration || '60 min'}</span>
            </div>
          </div>
          <span className="text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap"
            style={isCompleted ? { background: 'rgba(16,185,129,0.12)', color: '#059669' } : { background: 'rgba(29,78,216,0.12)', color: '#1D4ED8' }}
            data-testid="group-detail-status">{isCompleted ? 'Completed' : 'Upcoming'}</span>
        </div>

        {s.description && <p className="text-sm mt-4 leading-relaxed" style={muted}>{s.description}</p>}

        {!isCompleted && (
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="group-join-actions">
            {s.zoom_url ? (
              <a href={s.zoom_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-5 py-3 rounded-lg font-medium transition-colors" data-testid="group-join-zoom"><Video size={17} /> Join via Zoom <ExternalLink size={14} /></a>
            ) : (
              <button onClick={() => setShowRoom(true)} className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-5 py-3 rounded-lg font-medium transition-colors" data-testid="group-join-native"><Video size={17} /> Join group room</button>
            )}
          </div>
        )}
      </div>

      {showRoom && (
        <div className="p-card rounded-2xl p-3 mb-6" data-testid="group-native-room">
          <iframe title="Group room" src={jitsiUrl} allow="camera; microphone; fullscreen; display-capture; autoplay" className="w-full rounded-xl border-0" style={{ height: '70vh', minHeight: 480 }} />
        </div>
      )}

      {/* Recording */}
      {s.recording_url && (
        <div className="p-card rounded-2xl p-6 mb-6" data-testid="group-recording">
          <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><PlayCircle size={17} className="text-[#1D4ED8]" /> Session recording</h2>
          <Recording url={s.recording_url} />
        </div>
      )}

      {/* Recap */}
      {isCompleted && (hasRecap ? (
        <div className="space-y-6" data-testid="group-recap">
          {s.ai_summary && (
            <div className="p-card rounded-2xl p-6">
              <h2 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><FileText size={17} className="text-[#1D4ED8]" /> Group recap</h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={muted} data-testid="group-recap-summary">{s.ai_summary}</p>
            </div>
          )}
          {takeaways.length > 0 && (
            <div className="p-card rounded-2xl p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><CheckCircle2 size={17} className="text-emerald-500" /> Key takeaways</h2>
              <ul className="space-y-2.5" data-testid="group-recap-takeaways">
                {takeaways.map((t, i) => <li key={i} className="flex items-start gap-2.5 text-sm" style={muted}><CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" /> {t}</li>)}
              </ul>
            </div>
          )}
          {actionPlan.length > 0 && (
            <div className="p-card rounded-2xl p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><ListChecks size={17} className="text-[#F97316]" /> Group action plan</h2>
              <div className="space-y-3" data-testid="group-recap-action-plan">
                {actionPlan.map((a, i) => (
                  <div key={i} className="rounded-xl p-4 border" style={{ borderColor: 'var(--p-border)', background: 'var(--p-hover)' }}>
                    <div className="flex items-start gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-[#F97316] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <div>
                        <div className="font-medium text-sm" style={{ color: 'var(--p-text)' }}>{a.task || a}</div>
                        {a.why && <div className="text-xs mt-1" style={muted}>{a.why}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {goals.length > 0 && (
            <div className="p-card rounded-2xl p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><Target size={17} className="text-[#1D4ED8]" /> Goals the group is working toward</h2>
              <ul className="space-y-2.5" data-testid="group-recap-goals">
                {goals.map((g, i) => <li key={i} className="flex items-start gap-2.5 text-sm" style={muted}><Target size={15} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" /> {g}</li>)}
              </ul>
            </div>
          )}
          {s.notes && (
            <div className="p-card rounded-2xl p-6">
              <h2 className="font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Coach notes</h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={muted}>{s.notes}</p>
            </div>
          )}
          {s.transcript && (
            <div className="p-card rounded-2xl p-6">
              <button onClick={() => setShowTranscript((v) => !v)} className="w-full flex items-center justify-between" data-testid="group-toggle-transcript">
                <span className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><FileText size={17} style={muted} /> Full transcript</span>
                <ChevronDown size={18} className={`transition-transform ${showTranscript ? 'rotate-180' : ''}`} style={muted} />
              </button>
              {showTranscript && <p className="text-sm leading-relaxed whitespace-pre-wrap mt-4 pt-4 border-t" style={{ ...muted, borderColor: 'var(--p-border)' }} data-testid="group-transcript-text">{s.transcript}</p>}
            </div>
          )}
        </div>
      ) : (!s.recording_url && (
        <div className="p-card rounded-2xl p-10 text-center" data-testid="group-recap-pending">
          <FileText size={36} className="mx-auto mb-3" style={{ color: 'var(--p-faint)' }} />
          <p className="text-sm" style={muted}>The group recap, recording and action plan will appear here once the session is finalized.</p>
        </div>
      )))}
    </PortalLayout>
  );
}
